import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, Check, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export default function OrgSwitcher() {
  const {
    organisation,
    allOrganisations,
    hasMultipleAccessPaths,
    switchOrg,
  } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSwitch = (orgId: string) => {
    switchOrg(orgId);
    setOpen(false);
    navigate('/app', { replace: true });
  };

  if (!hasMultipleAccessPaths) {
    return (
      <div className="px-4 py-3">
        {organisation ? (
          <>
            <p className="text-[10px] text-app-chrome-muted uppercase font-semibold">Organisation</p>
            <p className="text-sm font-medium text-white truncate mt-0.5">{organisation.name}</p>
          </>
        ) : null}
      </div>
    );
  }

  const currentLabel = organisation?.name ?? 'Select workspace';

  return (
    <div className="relative px-2 py-2" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="app-focus-ring w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-white/[0.06] transition duration-150 group"
        aria-expanded={open}
      >
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-white/10">
          <Building2 className="w-3.5 h-3.5 text-app-chrome-muted" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-[10px] text-app-chrome-muted uppercase font-semibold leading-none">Workspace</p>
          <p className="text-xs font-medium text-white truncate mt-0.5">{currentLabel}</p>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-app-chrome-muted flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-2 right-2 top-full mt-1 z-50 overflow-hidden rounded-lg border border-white/15 bg-app-chrome">
          <div className="py-1.5 max-h-64 overflow-y-auto">
            {allOrganisations.map(option => {
              const isActive = organisation?.id === option.organisation.id;
              const isAdmin = option.membership.role === 'admin';
              return (
                <button
                  key={option.organisation.id}
                  onClick={() => handleSwitch(option.organisation.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                    isActive ? 'bg-white/10' : 'hover:bg-white/[0.06]'
                  }`}
                >
                  <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 bg-white/10">
                    <Building2 className="w-3 h-3 text-app-chrome-muted" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{option.organisation.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {isAdmin && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-app-chrome-muted">
                          <Shield className="w-2.5 h-2.5" /> Admin
                        </span>
                      )}
                    </div>
                  </div>
                  {isActive && <Check className="w-3.5 h-3.5 text-app-accent flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
